import React, { useState, useEffect } from 'react';
import {
  Clock, Save, RefreshCw, Wifi, Smartphone, Truck,
  Settings, Edit3, X
} from 'lucide-react';
import api from '../services/api';

/**
 * Página de configuración de intervalos.
 * - Defaults globales (config table)
 * - Override por device (devices table)
 * - Vista de jobs activos del scheduler
 */
const IntervalsConfig = () => {
  const [globals, setGlobals] = useState(null);
  const [devices, setDevices] = useState([]);
  const [jobs, setJobs]       = useState([]);
  const [editing, setEditing] = useState(null); // device_id en edición
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [g, d, j] = await Promise.all([
        api.get('/api/intervals/global'),
        api.get('/api/devices'),
        api.get('/api/scheduler/jobs'),
      ]);
      setGlobals(g.data);
      setDevices(d.data || []);
      setJobs(j.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const saveGlobals = async () => {
    try {
      await api.put('/api/intervals/global', globals);
      alert('✅ Globales guardados (aplica en ≤5min)');
      load();
    } catch (e) { alert('Error: ' + e.message); }
  };

  const reevaluate = async () => {
    await api.post('/api/scheduler/reevaluate');
    setTimeout(load, 1500);
  };

  // Map jobs por device_id
  const jobsMap = jobs.reduce((m, j) => { m[j.device_id] = j; return m; }, {});

  return (
    <div style={S.container}>
      <header style={S.header}>
        <div>
          <h1 style={S.title}><Clock size={26} /> Intervalos de captura</h1>
          <p style={S.subtitle}>Defaults por tipo de conexión + override por device</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={S.btn} onClick={reevaluate}>
            <RefreshCw size={16}/> Re-evaluar ahora
          </button>
          <button style={S.btn} onClick={load}>
            <RefreshCw size={16} className={loading ? 'spin' : ''}/> Actualizar
          </button>
        </div>
      </header>

      {/* Defaults globales */}
      {globals && (
        <div style={S.card}>
          <h3 style={S.cardTitle}>
            <Settings size={18}/> Defaults globales (segundos)
          </h3>
          <p style={S.help}>
            Se aplican cuando el device no tiene override. Cambios surten efecto en ≤5min.
          </p>
          <div style={S.grid}>
            <IntervalInput
              label="WiFi sucursal"
              icon={<Wifi size={16}/>}
              color="#0891b2"
              value={globals.wifi_snapshot_interval_seconds}
              onChange={v => setGlobals({...globals, wifi_snapshot_interval_seconds: v})}
            />
            <IntervalInput
              label="Datos celular"
              icon={<Smartphone size={16}/>}
              color="#ca8a04"
              value={globals.cellular_snapshot_interval_seconds}
              onChange={v => setGlobals({...globals, cellular_snapshot_interval_seconds: v})}
            />
            <IntervalInput
              label="Samsara"
              icon={<Smartphone size={16}/>}
              color="#7c3aed"
              value={globals.samsara_snapshot_interval_seconds}
              onChange={v => setGlobals({...globals, samsara_snapshot_interval_seconds: v})}
            />
            <IntervalInput
              label="Desconocido"
              icon={<Settings size={16}/>}
              color="#6b7280"
              value={globals.unknown_snapshot_interval_seconds}
              onChange={v => setGlobals({...globals, unknown_snapshot_interval_seconds: v})}
            />
          </div>
          <button style={{ ...S.btnPrimary, marginTop: 14 }} onClick={saveGlobals}>
            <Save size={16}/> Guardar globales
          </button>
        </div>
      )}

      {/* Override por device */}
      <div style={S.card}>
        <h3 style={S.cardTitle}>
          <Truck size={18}/> Override por device ({devices.length})
        </h3>
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Device</th>
                <th style={S.th}>Adaptativo</th>
                <th style={S.th}>WiFi</th>
                <th style={S.th}>Datos</th>
                <th style={S.th}>Samsara</th>
                <th style={S.th}>Conn actual</th>
                <th style={S.th}>Intervalo aplicado</th>
                <th style={S.th}></th>
              </tr>
            </thead>
            <tbody>
              {devices.length === 0 && (
                <tr><td colSpan={8} style={S.empty}>Sin devices</td></tr>
              )}
              {devices.map(d => {
                const job = jobsMap[d.device_id];
                return (
                  <DeviceRow
                    key={d.id}
                    device={d}
                    job={job}
                    isEditing={editing === d.device_id}
                    onEdit={() => setEditing(d.device_id)}
                    onCancel={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

// ============ Componente: input intervalo con presets ============
const IntervalInput = ({ label, icon, color, value, onChange }) => {
  const presets = [
    { l: '30s',  v: 30 },
    { l: '1min', v: 60 },
    { l: '5min', v: 300 },
    { l: '15m',  v: 900 },
    { l: '1h',   v: 3600 },
  ];
  return (
    <div style={{ background: 'white', padding: 14, borderRadius: 8, borderLeft: `4px solid ${color}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, color }}>
        {icon}<strong>{label}</strong>
      </div>
      <input
        type="number" min="10" value={value || ''}
        onChange={e => onChange(parseInt(e.target.value) || 60)}
        style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb',
                 borderRadius: 6, fontSize: 16, fontWeight: 600 }}
      />
      <div style={{ display: 'flex', gap: 4, marginTop: 8, flexWrap: 'wrap' }}>
        {presets.map(p => (
          <button key={p.v} onClick={() => onChange(p.v)}
                  style={{
                    padding: '4px 8px', fontSize: 11, border: 'none',
                    background: value === p.v ? color : '#f3f4f6',
                    color: value === p.v ? 'white' : '#374151',
                    borderRadius: 4, cursor: 'pointer'
                  }}>
            {p.l}
          </button>
        ))}
      </div>
    </div>
  );
};

// ============ Componente: fila editable de device ============
const DeviceRow = ({ device, job, isEditing, onEdit, onCancel, onSaved }) => {
  const [cfg, setCfg] = useState({
    use_adaptive_interval: device.use_adaptive_interval !== false,
    interval_wifi:     device.interval_wifi || '',
    interval_cellular: device.interval_cellular || '',
    interval_samsara:  device.interval_samsara || '',
  });

  useEffect(() => {
    if (isEditing) {
      setCfg({
        use_adaptive_interval: device.use_adaptive_interval !== false,
        interval_wifi:     device.interval_wifi || '',
        interval_cellular: device.interval_cellular || '',
        interval_samsara:  device.interval_samsara || '',
      });
    }
  }, [isEditing]);

  const save = async () => {
    const payload = {
      use_adaptive_interval: cfg.use_adaptive_interval,
      interval_wifi:     cfg.interval_wifi     ? parseInt(cfg.interval_wifi)     : null,
      interval_cellular: cfg.interval_cellular ? parseInt(cfg.interval_cellular) : null,
      interval_samsara:  cfg.interval_samsara  ? parseInt(cfg.interval_samsara)  : null,
    };
    try {
      await api.put(`/api/devices/${device.device_id}/intervals`, payload);
      onSaved();
    } catch (e) { alert('Error: ' + (e.response?.data?.detail || e.message)); }
  };

  if (isEditing) {
    return (
      <tr style={{ background: '#fef9c3' }}>
        <td style={{ ...S.td, fontWeight: 600 }}>{device.name}</td>
        <td style={S.td}>
          <input type="checkbox" checked={cfg.use_adaptive_interval}
                 onChange={e => setCfg({...cfg, use_adaptive_interval: e.target.checked})}/>
        </td>
        <td style={S.td}>
          <input type="number" placeholder="default" value={cfg.interval_wifi}
                 onChange={e => setCfg({...cfg, interval_wifi: e.target.value})}
                 style={S.inputSmall}/>
        </td>
        <td style={S.td}>
          <input type="number" placeholder="default" value={cfg.interval_cellular}
                 onChange={e => setCfg({...cfg, interval_cellular: e.target.value})}
                 style={S.inputSmall}/>
        </td>
        <td style={S.td}>
          <input type="number" placeholder="default" value={cfg.interval_samsara}
                 onChange={e => setCfg({...cfg, interval_samsara: e.target.value})}
                 style={S.inputSmall}/>
        </td>
        <td style={S.td} colSpan={2}>—</td>
        <td style={S.td}>
          <div style={{ display: 'flex', gap: 4 }}>
            <button style={S.btnSave} onClick={save}><Save size={12}/></button>
            <button style={S.btnCancel} onClick={onCancel}><X size={12}/></button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td style={{ ...S.td, fontWeight: 600 }}>
        {device.name}
        <div style={{ fontSize: 11, color: '#6b7280', fontFamily: 'monospace' }}>
          {device.device_id}
        </div>
      </td>
      <td style={S.td}>
        {device.use_adaptive_interval !== false
          ? <span style={S.adaptOn}>ON</span>
          : <span style={S.adaptOff}>OFF</span>}
      </td>
      <td style={S.td}>{device.interval_wifi || <em style={S.def}>default</em>}</td>
      <td style={S.td}>{device.interval_cellular || <em style={S.def}>default</em>}</td>
      <td style={S.td}>{device.interval_samsara || <em style={S.def}>default</em>}</td>
      <td style={S.td}>
        {job ? <ConnTypeBadge type={job.connection_type} /> : <em style={S.def}>—</em>}
      </td>
      <td style={S.td}>
        {job ? <span style={S.intervalApplied}>{job.interval}s</span>
             : <em style={S.def}>—</em>}
      </td>
      <td style={S.td}>
        <button style={S.btnEdit} onClick={onEdit}><Edit3 size={14}/></button>
      </td>
    </tr>
  );
};

const ConnTypeBadge = ({ type }) => {
  const cfg = {
    wifi_sucursal:  { bg: '#cffafe', fg: '#0e7490', label: 'WiFi' },
    samsara:        { bg: '#ede9fe', fg: '#5b21b6', label: 'Samsara' },
    datos_celular:  { bg: '#fef3c7', fg: '#92400e', label: 'Datos' },
    desconocido:    { bg: '#f3f4f6', fg: '#374151', label: '?' },
  }[type || 'desconocido'];
  return <span style={{ ...S.badge, background: cfg.bg, color: cfg.fg }}>{cfg.label}</span>;
};

const S = {
  container: { padding: 24, maxWidth: 1300, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  title: { fontSize: 24, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 10 },
  subtitle: { fontSize: 13, color: '#6b7280', marginTop: 4 },
  btn: { display: 'flex', gap: 6, alignItems: 'center', padding: '8px 14px',
         background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer' },
  btnPrimary: { padding: '8px 16px', background: '#3b82f6', color: 'white',
                border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14,
                display: 'flex', alignItems: 'center', gap: 6 },

  card: { background: 'white', padding: 20, borderRadius: 8, marginBottom: 16,
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
  cardTitle: { margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: 8, fontSize: 16 },
  help: { color: '#6b7280', fontSize: 13, marginTop: 0, marginBottom: 16 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 },

  tableWrap: { overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  th: { padding: '10px 12px', textAlign: 'left', background: '#f9fafb',
        borderBottom: '1px solid #e5e7eb', fontSize: 11,
        fontWeight: 600, color: '#374151', textTransform: 'uppercase' },
  td: { padding: '10px 12px', borderBottom: '1px solid #f3f4f6' },
  empty: { padding: 30, textAlign: 'center', color: '#9ca3af' },

  inputSmall: { width: 70, padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 },
  badge: { display: 'inline-flex', padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500 },
  def: { color: '#9ca3af', fontStyle: 'italic', fontSize: 12 },
  adaptOn: { background: '#d1fae5', color: '#065f46', padding: '2px 8px', borderRadius: 4, fontWeight: 600, fontSize: 11 },
  adaptOff: { background: '#fee2e2', color: '#991b1b', padding: '2px 8px', borderRadius: 4, fontWeight: 600, fontSize: 11 },
  intervalApplied: { fontWeight: 600, color: '#3b82f6' },

  btnEdit:   { padding: '4px 8px', background: '#dbeafe', color: '#1e40af',
               border: 'none', borderRadius: 4, cursor: 'pointer' },
  btnSave:   { padding: '4px 8px', background: '#10b981', color: 'white',
               border: 'none', borderRadius: 4, cursor: 'pointer' },
  btnCancel: { padding: '4px 8px', background: '#f3f4f6', color: '#374151',
               border: 'none', borderRadius: 4, cursor: 'pointer' },
};

export default IntervalsConfig;
